import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Alert, Input, Typography } from 'antd';
import { ArrowRight } from 'lucide-react';

import { Button } from '@/components/Button';
import { Logo } from '@/components/Logo';
import { resetAnalytics } from '@/services/analytics';
import { redeemAccessCode } from '@/services/api';
import { setStoredGuestSessionToken } from '@/utils/guestSession';
import { ROUTES } from '@/routes';
import styles from './Redeem.module.scss';

const { Title, Text } = Typography;

/**
 * Code-entry landing page for participants without an account — reached via
 * a shared link, code entered separately (see the module doc on
 * redeemAccessCode for why it's never read from the URL).
 *
 * Landing on the guest Sessions hub (/guest-interview — the same Sessions.tsx
 * a normal purchaser sees, just under its own URL; see
 * hooks/useSessionRoutes.ts) afterward is deliberate: redemption only
 * purchases the package, never starting an Assessment or HTS submission, so
 * the guest gets the same hub and the same HTS-or-interview choice a normal
 * purchaser gets instead of this page guessing which to force them into.
 */
export const Redeem: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Sent so the backend can reject a redemption made from a signed-in account
  // before it burns the code — null for the ordinary, signed-out candidate.
  const { getToken: getClerkToken } = useAuth();
  // Set by ProtectedRoute when a stored guest session token was just
  // rejected — first-time visitors get the default copy below instead.
  const sessionExpired = Boolean((location.state as { sessionExpired?: boolean } | null)?.sessionExpired);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setSubmitting(true);
    setError(null);
    try {
      const { sessionToken } = await redeemAccessCode(code.trim(), await getClerkToken());
      // A lapsed guest on a shared device may still be identified — drop them
      // so this guest's events aren't filed under the previous one.
      resetAnalytics();
      setStoredGuestSessionToken(sessionToken);
      navigate(ROUTES.GUEST_SESSIONS, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.column}>
        <Logo mode="light" className={styles.logo} />

        <div className={styles.card}>
          <Title level={2} className={styles.title}>
            Enter your access code
          </Title>
          <Text className={styles.subtitle}>
            {sessionExpired
              ? 'Your session has expired. Please re-enter your access code to continue viewing your results.'
              : 'You should have been given a one-time code separately from this link. Enter it below to start.'}
          </Text>

          {error && <Alert className={styles.alert} type="error" message={error} showIcon />}

          <form onSubmit={handleSubmit} className={styles.form}>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ACCESS CODE"
              size="large"
              autoFocus
              autoComplete="off"
              disabled={submitting}
              className={styles.codeInput}
            />

            <Button
              appearance="solid"
              htmlType="submit"
              size="large"
              block
              loading={submitting}
              disabled={!code.trim()}
              icon={<ArrowRight style={{ width: '1em', height: '1em' }} />}
              iconPlacement="end"
            >
              Start
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Redeem;
